<template>
  <Message
    v-bind="$attrs"
    :severity="props.severity"
    :pt="{
      root: rootClass,
      icon: iconClass,
      text: textClass,
    }"
  >
    <slot />
  </Message>
</template>

<script setup lang="ts">
import { Message } from 'primevue';
import { computed } from 'vue';

interface Props {
  severity?: 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';
  variant?: 'default' | 'soft';
}

const props = withDefaults(defineProps<Props>(), {
  severity: 'info',
  variant: 'soft',
});

const rootClass = computed(() => {
  if (props.variant === 'soft') {
    // Soft variant — tinted background using the theme-aware --p-*-soft-*
    // tokens (main.scss), so this flips correctly between light and dark
    // rather than needing separate class sets per theme.
    switch (props.severity) {
      case 'error':
        return '[background-color:var(--p-danger-soft-bg)] border [border-color:var(--p-danger-soft-border)] [color:var(--p-danger-soft-text)]';
      case 'success':
        return '[background-color:var(--p-success-soft-bg)] border [border-color:var(--p-success-soft-border)] [color:var(--p-success-soft-text)]';
      case 'warn':
        return '[background-color:var(--p-warning-soft-bg)] border [border-color:var(--p-warning-soft-border)] [color:var(--p-warning-soft-text)]';
      case 'info':
        return '[background-color:var(--p-info-soft-bg)] border [border-color:var(--p-info-soft-border)] [color:var(--p-info-soft-text)]';
      case 'secondary':
        return 'bg-surface-100/60 border border-surface-300/50 text-surface-800';
      default:
        return 'bg-surface-50/80 border border-surface-200 text-surface-800';
    }
  } else {
    // Default variant with solid backgrounds
    switch (props.severity) {
      case 'error':
        return 'bg-red-600 border border-red-700 text-white';
      case 'success':
        return 'bg-green-600 border border-green-700 text-white';
      case 'warn':
        return 'bg-yellow-600 border border-yellow-700 text-white';
      case 'info':
        return 'bg-blue-600 border border-blue-700 text-white';
      case 'secondary':
        return 'bg-surface-200 border border-surface-300 text-surface-800';
      default:
        return 'bg-surface-100 border border-surface-200 text-surface-800';
    }
  }
});

const iconClass = computed(() => {
  if (props.variant === 'soft') {
    switch (props.severity) {
      case 'error':
        return 'text-[var(--p-danger-soft-icon)]';
      case 'success':
        return 'text-[var(--p-success-soft-icon)]';
      case 'warn':
        return 'text-[var(--p-warning-soft-icon)]';
      case 'info':
        return 'text-[var(--p-info-soft-icon)]';
      default:
        return 'text-surface-500';
    }
  } else {
    return 'text-white';
  }
});

const textClass = computed(() => {
  if (props.variant === 'soft') {
    switch (props.severity) {
      case 'error':
        return 'text-[var(--p-danger-soft-text)]';
      case 'success':
        return 'text-[var(--p-success-soft-text)]';
      case 'warn':
        return 'text-[var(--p-warning-soft-text)]';
      case 'info':
        return 'text-[var(--p-info-soft-text)]';
      default:
        return 'text-surface-800';
    }
  } else {
    return 'text-white';
  }
});
</script>

<style scoped></style>
